import React from 'react';
import { reportError } from '../services/errorReporter';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });

    // Always log to console for the dev tools / logged-in support staff
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Transport to backend so log_watcher.py / the admin client-errors
    // page can pick it up
    reportError({
      message: error?.message || String(error),
      stack: error?.stack,
      source: 'ErrorBoundary',
      componentStack: errorInfo?.componentStack,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  handleReload = () => {
    // Hard reload — more reliable than handleReset for state-corrupted
    // pages because it tears down the entire React tree and re-fetches
    // bundled JS from the server.
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  toggleDetails = () => {
    this.setState((s) => ({ showDetails: !s.showDetails }));
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="error-boundary" role="alert">
        <div className="error-boundary__icon" aria-hidden="true">!</div>
        <h2>Something went wrong</h2>
        <p className="error-boundary__message">
          The page hit an unexpected error. The team has been notified.
          Try reloading or going back to the search page.
        </p>

        <div className="error-boundary__actions">
          <button
            type="button"
            onClick={this.handleReload}
            className="btn btn-primary"
          >
            Reload page
          </button>
          <a href="/" className="btn btn-outline">
            Go to Search
          </a>
          <button
            type="button"
            onClick={this.handleReset}
            className="btn btn-outline"
          >
            Try again
          </button>
        </div>

        <button
          type="button"
          onClick={this.toggleDetails}
          className="error-boundary__details-toggle"
          aria-expanded={this.state.showDetails}
        >
          {this.state.showDetails ? 'Hide technical details' : 'Show technical details'}
        </button>

        {this.state.showDetails && (
          <div className="error-boundary__details">
            {this.state.error && (
              <>
                <h3>Error</h3>
                <pre>{this.state.error.toString()}</pre>
              </>
            )}
            {this.state.errorInfo?.componentStack && (
              <>
                <h3>Component stack</h3>
                <pre>{this.state.errorInfo.componentStack}</pre>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
